import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useIsMobile } from '@/hooks/use-mobile';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';
import { useClientPhotos } from '@/hooks/useClientPhotos';
import {
  useBedCountEntries,
  useMarkBedCountEntry,
  useRequestSignOff,
  useSignOffBedCount,
  useStartBedCount,
  type BedCount,
  type BedCountEntry,
  type BedCountResult,
} from '@/hooks/useBedCount';
import { useBedCountConfig } from '@/hooks/useBedCountConfig';
import { Check, X, BedDouble, Printer, ChevronDown, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { FamilyUnitRow } from './FamilyUnitRow';

interface BedCountSheetProps {
  bedCount: BedCount;
  siteId: string;
  siteName?: string;
  canSignOff?: boolean;
  onPrint?: () => void;
}

export const BedCountSheet = ({
  bedCount,
  siteId,
  siteName,
  canSignOff = false,
  onPrint,
}: BedCountSheetProps) => {
  const isMobile = useIsMobile();
  const { clients } = useClients();
  const { rooms } = useRooms();
  const { getClientPhoto } = useClientPhotos(clients);
  const { data: entries = [] } = useBedCountEntries(bedCount.id);
  const { data: cfgData } = useBedCountConfig(siteId);
  const protocol = cfgData?.config.protocol ?? 'SA';
  const startMut = useStartBedCount();
  const markMut = useMarkBedCountEntry();
  const requestSignOffMut = useRequestSignOff();
  const signOffMut = useSignOffBedCount();

  const completedByIds = useMemo(
    () => Array.from(new Set(entries.map((e) => e.marked_by).filter(Boolean) as string[])),
    [entries]
  );

  // Resolve names for who started / completed / signed off
  const userIds = Array.from(new Set([bedCount.started_by, bedCount.signed_off_by, ...completedByIds].filter(Boolean) as string[]));
  const { data: userNames = {} } = useQuery({
    queryKey: ['bed-count-user-names', userIds.sort().join(',')],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((p: any) => { map[p.id] = p.full_name || 'Unknown'; });
      return map;
    },
  });
  const startedByName = bedCount.started_by ? (userNames[bedCount.started_by] || 'Loading…') : null;
  const signedOffByName = bedCount.signed_off_by ? (userNames[bedCount.signed_off_by] || 'Loading…') : null;
  const completedByNames = completedByIds.map((id) => userNames[id] || 'Loading…').join(', ');

  const clientById = useMemo(() => {
    const m = new Map<string, any>();
    clients.forEach((c: any) => m.set(c.id, c));
    return m;
  }, [clients]);

  const roomById = useMemo(() => {
    const m = new Map<string, any>();
    rooms.forEach((r: any) => m.set(r.id, r));
    return m;
  }, [rooms]);

  // Group entries: for FwC, group by family unit (HOH + partner + children).
  const grouped = useMemo(() => {
    const passEntries: BedCountEntry[] = [];
    const regular: BedCountEntry[] = [];
    entries.forEach((e) => {
      if (e.expected_status === 'on_pass') passEntries.push(e);
      else regular.push(e);
    });
    regular.sort((a, b) => {
      const ra = roomById.get(a.room_id ?? '')?.room_number ?? '';
      const rb = roomById.get(b.room_id ?? '')?.room_number ?? '';
      return String(ra).localeCompare(String(rb), undefined, { numeric: true });
    });

    if (protocol !== 'FWC') {
      return { regular, passEntries, families: [] as { hoh: BedCountEntry; members: BedCountEntry[] }[] };
    }

    // FwC: build family units
    const entryByClient = new Map<string, BedCountEntry>();
    regular.forEach((e) => entryByClient.set(e.client_id, e));

    const consumed = new Set<string>();
    const families: { hoh: BedCountEntry; members: BedCountEntry[] }[] = [];
    const singles: BedCountEntry[] = [];

    regular.forEach((entry) => {
      if (consumed.has(entry.id)) return;
      const client = clientById.get(entry.client_id);
      if (client?.is_head_of_household) {
        consumed.add(entry.id);
        const members: BedCountEntry[] = [];
        if (client.partner_id) {
          const partnerEntry = entryByClient.get(client.partner_id);
          if (partnerEntry && !consumed.has(partnerEntry.id)) {
            members.push(partnerEntry);
            consumed.add(partnerEntry.id);
          }
        }
        // Children entries linked via separate client rows would also live in regular
        // (they are matched by partner_id back to HOH only in some setups; we keep
        // the simple HOH+partner pairing here and let staff expand to mark children).
        families.push({ hoh: entry, members });
      } else if (!client?.partner_id) {
        // standalone non-HOH — treat as single
        singles.push(entry);
        consumed.add(entry.id);
      }
    });

    // Anything left (e.g. non-HOH partner whose HOH wasn't in the count)
    regular.forEach((e) => { if (!consumed.has(e.id)) singles.push(e); });

    return { regular: singles, passEntries, families };
  }, [entries, roomById, protocol, clientById]);

  const marked = entries.filter((e) => e.result).length;
  const total = entries.length;
  const allMarked = total > 0 && marked === total;

  // Pending state — needs to be started
  if (bedCount.status === 'pending') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BedDouble className="h-5 w-5" /> Bed Count
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            <div><span className="font-medium text-foreground">{siteName ?? 'Facility'}</span></div>
            <div>Scheduled: {new Date(bedCount.scheduled_for).toLocaleString()}</div>
            <div className="mt-1">
              Protocol: <Badge variant="outline">{protocol === 'FWC' ? 'Adult Family' : 'Single Adult'}</Badge>
            </div>
          </div>
          <Button
            onClick={() =>
              startMut.mutate(
                { bedCountId: bedCount.id, siteId },
                {
                  onSuccess: ({ entryCount }) => {
                    toast.success(
                      entryCount > 0
                        ? `Bed count started with ${entryCount} clients`
                        : 'Bed count started — no bedded clients found'
                    );
                  },
                  onError: (err: any) => {
                    toast.error(err.message || 'Failed to start bed count');
                    console.error('Start bed count error:', err);
                  },
                }
              )
            }
            disabled={startMut.isPending}
            size="lg"
          >
            {startMut.isPending ? 'Starting...' : 'Start Bed Count'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleMark = (entryId: string, result: BedCountResult) => {
    markMut.mutate({ entryId, result });
  };

  const renderEntryRow = (entry: BedCountEntry, isPass = false) => {
    const client = clientById.get(entry.client_id);
    const room = roomById.get(entry.room_id ?? '');
    const fullName = client
      ? (`${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.name?.trim() || 'Unknown')
      : 'Unknown';
    const photoUrl = getClientPhoto(entry.client_id);

    return (
      <div
        key={entry.id}
        className={cn(
          'border rounded-lg p-3 transition-colors',
          entry.result === 'present' && 'bg-primary/5 border-primary/30',
          entry.result === 'not_present' && 'bg-destructive/5 border-destructive/30',
          entry.result === 'unbedded' && 'bg-muted',
          entry.result === 'pass_acknowledged' && 'bg-secondary/40'
        )}
      >
        <div className={cn('flex gap-3', isMobile ? 'flex-col' : 'items-center')}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarImage src={photoUrl ?? undefined} alt={fullName} />
              <AvatarFallback><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{fullName}</div>
              <div className="text-xs text-muted-foreground truncate">
                Room {room?.room_number ?? '—'}
                {isPass && <Badge variant="outline" className="ml-2">On Pass</Badge>}
              </div>
            </div>
          </div>

          <div className={cn('flex gap-2', isMobile && 'w-full')}>
            {isPass ? (
              <Button
                size={isMobile ? 'lg' : 'sm'}
                variant={entry.result === 'pass_acknowledged' ? 'default' : 'outline'}
                onClick={() => handleMark(entry.id, 'pass_acknowledged')}
                className={cn(isMobile && 'flex-1 min-h-[56px]')}
              >
                <Check className="h-4 w-4" />
                Acknowledge
              </Button>
            ) : (
              <>
                <Button
                  size={isMobile ? 'lg' : 'sm'}
                  variant={entry.result === 'present' ? 'default' : 'outline'}
                  onClick={() => handleMark(entry.id, 'present')}
                  className={cn(isMobile && 'flex-1 min-h-[56px]')}
                >
                  <Check className="h-4 w-4" />
                  Present
                </Button>
                <Button
                  size={isMobile ? 'lg' : 'sm'}
                  variant={entry.result === 'not_present' ? 'destructive' : 'outline'}
                  onClick={() => handleMark(entry.id, 'not_present')}
                  className={cn(isMobile && 'flex-1 min-h-[56px]')}
                >
                  <X className="h-4 w-4" />
                  Not Present
                </Button>
                <Button
                  size={isMobile ? 'lg' : 'sm'}
                  variant={entry.result === 'unbedded' ? 'secondary' : 'outline'}
                  onClick={() => handleMark(entry.id, 'unbedded')}
                  className={cn(isMobile && 'flex-1 min-h-[56px]')}
                >
                  Unbed
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="sticky top-0 z-10 bg-card border-b">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BedDouble className="h-5 w-5" /> Bed Count
              <Badge variant="outline" className="ml-2 text-xs font-normal">
                {protocol === 'FWC' ? 'Adult Family' : 'Single Adult'}
              </Badge>
            </CardTitle>
            <div className="text-sm text-muted-foreground mt-1">
              {siteName ?? 'Facility'} · {new Date(bedCount.scheduled_for).toLocaleString()}
            </div>
            {bedCount.started_at && (
              <div className="text-xs text-muted-foreground">
                Started {new Date(bedCount.started_at).toLocaleTimeString()}
                {startedByName && <> by <span className="font-medium text-foreground">{startedByName}</span></>}
              </div>
            )}
            {bedCount.signed_off_at && (
              <div className="text-xs text-muted-foreground">
                Signed off {new Date(bedCount.signed_off_at).toLocaleTimeString()}
                {signedOffByName && <> by <span className="font-medium text-foreground">{signedOffByName}</span></>}
              </div>
            )}
            {completedByNames && (
              <div className="text-xs text-muted-foreground">
                Completed by <span className="font-medium text-foreground">{completedByNames}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{marked} / {total} confirmed</Badge>
            {onPrint && (
              <Button variant="outline" size="sm" onClick={onPrint}>
                <Printer className="h-4 w-4" />
                Print
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 pt-4">
        {protocol === 'FWC' && grouped.families.length > 0 && (
          <div className="space-y-2">
            {grouped.families.map((fam) => (
              <FamilyUnitRow
                key={fam.hoh.id}
                hohEntry={fam.hoh}
                memberEntries={fam.members}
                clientById={clientById}
                roomById={roomById}
                getClientPhoto={getClientPhoto}
              />
            ))}
          </div>
        )}

        {grouped.regular.map((e) => renderEntryRow(e, false))}

        {grouped.passEntries.length > 0 && (
          <Collapsible defaultOpen={false}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 border rounded-lg hover:bg-muted/50 text-sm font-medium">
              <ChevronDown className="h-4 w-4" />
              On Pass ({grouped.passEntries.length})
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 mt-2">
              {grouped.passEntries.map((e) => renderEntryRow(e, true))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {total === 0 && (
          <div className="text-center text-muted-foreground py-8 text-sm">
            No bedded clients at this facility.
          </div>
        )}
      </CardContent>

      {(allMarked || bedCount.status === 'awaiting_signoff') && (
        <div className="sticky bottom-0 bg-card border-t p-4 flex items-center justify-between gap-2">
          <div className="text-sm">
            {bedCount.status === 'awaiting_signoff' ? (
              <span className="text-muted-foreground">Awaiting Supervisor sign-off</span>
            ) : (
              <span className="font-medium">Ready for sign-off</span>
            )}
          </div>
          {bedCount.status !== 'awaiting_signoff' && allMarked && (
            <Button
              variant="outline"
              onClick={() =>
                requestSignOffMut.mutate(bedCount.id, {
                  onSuccess: () => toast.success('Sign-off requested — supervisor notified'),
                  onError: (err: any) => toast.error(err.message || 'Failed to request sign-off'),
                })
              }
              disabled={requestSignOffMut.isPending}
            >
              {requestSignOffMut.isPending ? 'Requesting...' : 'Request Sign-Off'}
            </Button>
          )}
          {canSignOff && (
            <Button
              onClick={() =>
                signOffMut.mutate(
                  { bedCountId: bedCount.id, siteId },
                  {
                    onSuccess: () => toast.success('Bed count signed off and completed'),
                    onError: (err: any) => toast.error(err.message || 'Sign-off failed'),
                  }
                )
              }
              disabled={signOffMut.isPending || !allMarked}
            >
              {signOffMut.isPending ? 'Signing off...' : 'Sign Off'}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
